import React, { useState, useEffect } from 'react';
import { toast } from 'react-toastify';
import { companyOfficeAPI } from '../../services/api';

export default function CompanyOfficesPage() {
  const [links, setLinks]       = useState([]);
  const [loading, setLoading]   = useState(true);
  const [showSearch, setShowSearch] = useState(false);
  const [query, setQuery]       = useState('');
  const [results, setResults]   = useState([]);
  const [searching, setSearching] = useState(false);
  const [adding, setAdding]     = useState(null); // officeId being added

  const fetchLinks = async () => {
    setLoading(true);
    try {
      const res = await companyOfficeAPI.list();
      setLinks(res.data.data || []);
    } catch { toast.error('خطأ في تحميل المكاتب'); }
    finally { setLoading(false); }
  };

  useEffect(() => { fetchLinks(); }, []);

  const searchOffices = async (q) => {
    setQuery(q);
    if (!q.trim()) { setResults([]); return; }
    setSearching(true);
    try {
      const res = await companyOfficeAPI.search(q);
      setResults(res.data.data || []);
    } catch { toast.error('خطأ في البحث'); }
    finally { setSearching(false); }
  };

  const addOffice = async (officeId) => {
    setAdding(officeId);
    try {
      await companyOfficeAPI.add(officeId);
      toast.success('تم ربط المكتب بنجاح');
      setResults((prev) => prev.filter((o) => o.id !== officeId));
      fetchLinks();
    } catch (err) {
      toast.error(err.response?.data?.message || 'خطأ في الربط');
    } finally { setAdding(null); }
  };

  const toggleLink = async (linkId, isActive) => {
    try {
      await companyOfficeAPI.toggle(linkId);
      toast.success(isActive ? 'تم إيقاف المكتب مؤقتاً' : 'تم تفعيل المكتب');
      fetchLinks();
    } catch { toast.error('خطأ في التحديث'); }
  };

  const removeLink = async (linkId) => {
    if (!window.confirm('هل أنت متأكد من إزالة هذا المكتب؟')) return;
    try {
      await companyOfficeAPI.remove(linkId);
      toast.success('تم إزالة المكتب');
      fetchLinks();
    } catch { toast.error('خطأ في الإزالة'); }
  };

  return (
    <div dir="rtl" className="p-4 md:p-6 max-w-3xl">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-xl font-bold text-gray-900">مكاتب الحجز الشريكة</h1>
          <p className="text-sm text-gray-500 mt-1">المكاتب المرخص لها ببيع تذاكر رحلاتك</p>
        </div>
        <button
          onClick={() => { setShowSearch(true); setQuery(''); setResults([]); }}
          className="bg-blue-600 text-white px-4 py-2.5 rounded-xl text-sm font-bold hover:bg-blue-700 transition flex items-center gap-2"
        >
          <span className="text-lg leading-none">+</span> إضافة مكتب
        </button>
      </div>

      {/* Add office search panel */}
      {showSearch && (
        <div className="bg-white rounded-2xl border p-5 mb-5 shadow-sm">
          <div className="flex items-center justify-between mb-3">
            <h2 className="font-bold text-gray-800 text-sm">البحث عن مكتب</h2>
            <button onClick={() => setShowSearch(false)} className="text-gray-400 hover:text-gray-700 text-xl leading-none">&times;</button>
          </div>
          <input
            className="w-full border rounded-xl px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-400 mb-3"
            placeholder="ابحث باسم المكتب أو المدينة..."
            value={query}
            onChange={(e) => searchOffices(e.target.value)}
            autoFocus
          />
          {searching && <p className="text-xs text-gray-400 text-center py-2">جاري البحث...</p>}
          {!searching && query && results.length === 0 && (
            <p className="text-xs text-gray-400 text-center py-2">لا توجد نتائج — جميع المكاتب المطابقة مرتبطة بالفعل</p>
          )}
          <div className="space-y-2 max-h-56 overflow-y-auto">
            {results.map((o) => (
              <div key={o.id} className="flex items-center justify-between p-3 bg-gray-50 rounded-xl border">
                <div>
                  <p className="font-semibold text-gray-800 text-sm">{o.name}</p>
                  <p className="text-xs text-gray-500">{o.city} · {o.phone}</p>
                </div>
                <button
                  onClick={() => addOffice(o.id)}
                  disabled={adding === o.id}
                  className="bg-blue-600 text-white px-3 py-1.5 rounded-lg text-xs font-bold hover:bg-blue-700 disabled:opacity-50 transition"
                >
                  {adding === o.id ? '...' : 'إضافة'}
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Linked offices list */}
      {loading ? (
        <div className="text-center py-12 text-gray-400">جاري التحميل...</div>
      ) : links.length === 0 ? (
        <div className="bg-white rounded-2xl border p-10 text-center">
          <div className="w-14 h-14 bg-blue-50 rounded-2xl flex items-center justify-center mx-auto mb-4">
            <svg className="w-7 h-7 text-blue-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.8}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M3 9l9-7 9 7v11a2 2 0 01-2 2H5a2 2 0 01-2-2V9z" />
              <path strokeLinecap="round" strokeLinejoin="round" d="M9 22V12h6v10" />
            </svg>
          </div>
          <p className="font-semibold text-gray-700">لا توجد مكاتب شريكة بعد</p>
          <p className="text-sm text-gray-400 mt-1">أضف مكتب حجز ليتمكن من بيع تذاكر رحلاتك</p>
        </div>
      ) : (
        <div className="space-y-3">
          {links.map((link) => (
            <div key={link.id} className="bg-white rounded-2xl border border-gray-200 p-4 flex items-center justify-between hover:shadow-sm transition">
              <div className="flex items-center gap-3">
                <div className={`w-10 h-10 rounded-xl flex items-center justify-center text-sm font-bold flex-shrink-0 ${link.isActive ? 'bg-blue-100 text-blue-700' : 'bg-gray-100 text-gray-400'}`}>
                  {link.office?.name?.[0]}
                </div>
                <div>
                  <p className="font-semibold text-gray-900 text-sm">{link.office?.name}</p>
                  <p className="text-xs text-gray-500">{link.office?.city} · {link.office?.phone}</p>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <span className={`px-2.5 py-1 rounded-full text-xs font-bold ${link.isActive ? 'bg-emerald-100 text-emerald-700' : 'bg-gray-100 text-gray-500'}`}>
                  {link.isActive ? 'نشط' : 'موقوف'}
                </span>
                <button
                  onClick={() => toggleLink(link.id, link.isActive)}
                  className="text-xs text-blue-600 hover:text-blue-800 font-semibold px-2 py-1 rounded-lg hover:bg-blue-50 transition"
                >
                  {link.isActive ? 'إيقاف' : 'تفعيل'}
                </button>
                <button
                  onClick={() => removeLink(link.id)}
                  className="text-xs text-red-500 hover:text-red-700 font-semibold px-2 py-1 rounded-lg hover:bg-red-50 transition"
                >
                  إزالة
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {links.length > 0 && (
        <div className="mt-5 bg-amber-50 border border-amber-200 rounded-2xl p-4 text-sm text-amber-800">
          <p className="font-semibold mb-1">ملاحظة:</p>
          <p>المكاتب النشطة فقط تستطيع البحث عن رحلاتك وإصدار التذاكر. إيقاف مكتب يمنعه من الحجز مباشرة دون حذف سجله.</p>
        </div>
      )}
    </div>
  );
}
